import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

import { api, type Payment } from '../api/client';
import { T } from '../styles/tokens';
import { haptic } from '../telegram/sdk';

interface Props {
  onBack: () => void;
}

const statusMeta: Record<Payment['status'], { label: string; color: string }> = {
  paid: { label: 'Оплачен', color: T.success },
  pending: { label: 'Ожидает', color: T.warn },
  failed: { label: 'Ошибка', color: T.error },
  trial: { label: 'Триал', color: T.accent },
};

export default function Payments({ onBack }: Props) {
  const [rows, setRows] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.payments
      .list()
      .then(setRows)
      .catch((e) => setErr(e instanceof Error ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={p.wrap}>
      <div style={p.header}>
        <button style={p.backBtn} onClick={() => { haptic('light'); onBack(); }}>← Назад</button>
        <div style={p.headTag}>// финансы</div>
        <div style={p.headTitle}>История платежей</div>
      </div>

      {loading && <div style={p.empty}>Загрузка...</div>}
      {err && <div style={p.err}>⚠ {err}</div>}
      {!loading && !err && rows.length === 0 && <div style={p.empty}>Платежей пока нет</div>}

      <div style={p.list}>
        {rows.map((r) => {
          const meta = statusMeta[r.status] || statusMeta.pending;
          return (
            <div key={r.id} style={p.row}>
              <div style={p.rowTop}>
                <div style={p.plan}>{r.plan_label}</div>
                <div style={{ ...p.amount }}>
                  {r.amount > 0 ? `${r.amount} ₽` : '—'}
                </div>
              </div>
              <div style={p.rowBottom}>
                <div style={p.meta}>
                  <span>{new Date(r.created_at).toLocaleDateString('ru', { day: '2-digit', month: '2-digit', year: '2-digit' })}</span>
                  <span> · </span>
                  <span>{r.protocol === 'wireguard' ? 'WireGuard' : r.protocol === 'vless' ? 'VLESS' : '—'}</span>
                </div>
                <span style={{ ...p.badge, color: meta.color, borderColor: meta.color + '55' }}>
                  {meta.label}
                </span>
              </div>
              <div style={p.orderId}>#{r.order_id}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const p: Record<string, CSSProperties> = {
  wrap: { padding: '16px 16px 24px' },
  header: { marginBottom: 18 },
  backBtn: { background: 'none', border: 'none', color: T.accent, fontFamily: 'DM Mono, monospace', fontSize: 12, padding: 0, cursor: 'pointer', marginBottom: 10 },
  headTag: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.accent, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 },
  headTitle: { fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: 24, letterSpacing: '-0.03em' },
  empty: { textAlign: 'center' as const, padding: '20px 0', color: T.textMuted, fontFamily: 'DM Mono, monospace', fontSize: 12 },
  err: { padding: '10px 12px', border: '1px solid oklch(0.58 0.19 27 / 0.5)', background: 'oklch(0.58 0.19 27 / 0.08)', color: 'oklch(0.72 0.19 27)', fontFamily: 'DM Mono, monospace', fontSize: 11, borderRadius: 3, marginBottom: 14 },
  list: { display: 'flex', flexDirection: 'column' as const, gap: 8 },
  row: { background: T.surface, border: `1px solid ${T.border}`, borderRadius: 4, padding: '12px 14px' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 },
  plan: { fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: 15, color: T.text },
  amount: { fontFamily: 'DM Mono, monospace', fontWeight: 600, fontSize: 14, color: T.text },
  rowBottom: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  meta: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted, letterSpacing: '0.04em' },
  badge: { fontFamily: 'DM Mono, monospace', fontSize: 9, fontWeight: 600, padding: '2px 6px', border: '1px solid', borderRadius: 2, textTransform: 'uppercase' as const, letterSpacing: '0.06em' },
  orderId: { fontFamily: 'DM Mono, monospace', fontSize: 10, color: T.textMuted },
};
